/**
 * Service Worker Manager
 * Handles PWA registration and offline capabilities
 */

class ServiceWorkerManager {
  private swRegistration: ServiceWorkerRegistration | null = null;

  async register(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service workers are not supported in this browser');
      return;
    }

    try {
      this.swRegistration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      console.log('Service Worker registered successfully:', this.swRegistration);

      // Handle updates
      this.swRegistration.addEventListener('updatefound', () => {
        this.handleUpdate();
      });

      // Check for updates every 10 minutes
      setInterval(() => {
        this.checkForUpdates();
      }, 10 * 60 * 1000);

    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }

  private handleUpdate(): void {
    if (!this.swRegistration) return;

    const newWorker = this.swRegistration.installing;
    if (!newWorker) return;

    newWorker.addEventListener('statechange', () => {
      if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
        // New version available
        this.showUpdateNotification();
      }
    });
  }

  private showUpdateNotification(): void {
    if (confirm('A new version is available. Reload to update?')) {
      this.skipWaiting();
    }
  }

  async checkForUpdates(): Promise<void> {
    if (this.swRegistration) {
      try {
        await this.swRegistration.update();
      } catch (error) {
        console.error('Failed to check for updates:', error);
      }
    }
  }

  skipWaiting(): void {
    if (this.swRegistration?.waiting) {
      this.swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  }

  async clearCache(): Promise<void> {
    return new Promise((resolve) => {
      if (this.swRegistration?.active) {
        const channel = new MessageChannel();
        channel.port1.onmessage = (event) => {
          if (event.data.success) {
            console.log('Cache cleared successfully');
            resolve();
          }
        };
        
        this.swRegistration.active.postMessage(
          { type: 'CLEAR_CACHE' },
          [channel.port2]
        );
      } else {
        resolve();
      }
    });
  }

  getInstallPrompt(): Promise<BeforeInstallPromptEvent | null> {
    return new Promise((resolve) => {
      let installPrompt: BeforeInstallPromptEvent | null = null;

      window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        installPrompt = e as BeforeInstallPromptEvent;
        resolve(installPrompt);
      });

      // If the event has already fired, resolve with null
      setTimeout(() => {
        if (!installPrompt) {
          resolve(null);
        }
      }, 1000);
    });
  }

  async installApp(): Promise<boolean> {
    const installPrompt = await this.getInstallPrompt();
    
    if (!installPrompt) {
      console.log('Install prompt not available');
      return false;
    }

    try {
      const result = await installPrompt.prompt();
      console.log('Install prompt result:', result);
      return result.outcome === 'accepted';
    } catch (error) {
      console.error('Install prompt failed:', error);
      return false;
    }
  }

  isInstalled(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.matchMedia('(display-mode: fullscreen)').matches ||
           (window.navigator as any).standalone === true;
  }

  getConnectionStatus(): boolean {
    return navigator.onLine;
  }

  onConnectionChange(callback: (isOnline: boolean) => void): () => void {
    const handleOnline = () => callback(true);
    const handleOffline = () => callback(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export const serviceWorkerManager = new ServiceWorkerManager();